import React, { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";

/**
 * MarkerCluster wrapper. Renders a leaflet.markercluster group and mounts
 * markers by calling addLayer for each report. On zoom/spider it lets users
 * pick a single marker.
 *
 * `clusterPalette` may be either:
 *   - null/undefined → default green gradient (used for environmental reports);
 *   - a function receiving the array of child markers and returning
 *     `{from, to, shadow}` hex colors (used to color marketplace clusters by
 *     their content type: viola=Vendo, blu=Regalo, marrone=Riutilizzabile,
 *     grigio-neutro=misto).
 */
const DEFAULT_PALETTE = { from: "#10B981", to: "#065F46", shadow: "rgba(6,95,70,.35)" };

export default function MarkerClusterGroup({ reports, buildMarker, clusterPalette }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !reports) return;
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: 17,
      maxClusterRadius: 55,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        const size = count < 10 ? 34 : count < 50 ? 42 : 52;
        let palette = DEFAULT_PALETTE;
        try {
          if (typeof clusterPalette === "function") {
            palette = clusterPalette(cluster.getAllChildMarkers()) || DEFAULT_PALETTE;
          } else if (clusterPalette && typeof clusterPalette === "object") {
            palette = clusterPalette;
          }
        } catch { /* fall back to default */ }
        return L.divIcon({
          html: `<div style="width:${size}px;height:${size}px;border-radius:9999px;background:linear-gradient(135deg,${palette.from},${palette.to});color:#fff;font-family:'DM Sans',sans-serif;font-weight:700;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 6px 18px ${palette.shadow};font-size:${count < 100 ? 14 : 12}px;">${count}</div>`,
          className: "tc-cluster",
          iconSize: [size, size],
        });
      },
    });
    reports.forEach((r) => {
      const m = buildMarker(r);
      if (m) group.addLayer(m);
    });
    map.addLayer(group);
    return () => {
      map.removeLayer(group);
    };
  }, [map, reports, buildMarker, clusterPalette]);
  return null;
}
